import {Component, OnDestroy, OnInit} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';

/**
 * AI:
 * Status: 'in progress'
 * Type: ObjectComponent
 * SubType: Detail
 * Reason: Object component for a dunning proposal (bd.DunningProposal) — the review-gate
 *         record between the determination run (writes PENDING proposals) and the
 *         processing run (executes ONLY approved proposals). Opened as the 'main' view via
 *         the framework navigation service from both proposal surfaces (review tab +
 *         dashboard workbench).
 *
 *         Renders the entity's own read-only data widget, which lays out the backend
 *         FormObject field groups (GENERAL/BALANCE/APPROVAL/PROCESSING/RELATION/TECH →
 *         Allgemein/Saldo/Freigabe/Verarbeitung/Verknüpfungen/Technische Daten). The
 *         group labels come from the backend form metadata, so they are not hard-coded
 *         here.
 */
@Component({
    selector: 'bd-dunning-proposal-object',
    templateUrl: './dunning-proposal-object.component.html',
    styleUrls: ['./dunning-proposal-object.component.scss'],
    standalone: false
})
export class DunningProposalObjectComponent extends ObjectBaseComponent implements OnInit, OnDestroy {

    /** Read-only data widget rendering the proposal's backend field groups. */
    proposalDataWidget: WidgetData;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected observerService: ObserverService
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    ngOnInit(): void {
        super.ngOnInit();
    }

    /**
     * Called when the proposal object is (re)loaded. Sets the page title and (re)builds
     * the read-only data widget. MUST NOT call refreshObject() (infinite loop).
     */
    onObjectChanged(): void {
        const proposalId = this.dto?.id ?? this.objectIdentifier?.objectId;
        this.setPageTitle(`Mahnvorschlag: ${proposalId ?? ''}`.trim());
        this.createWidgets(proposalId);
    }

    /**
     * Builds the read-only data widget for the proposal. The backend FormObject drives
     * the field-group layout, so this single data widget IS the grouped detail view.
     */
    private createWidgets(proposalId: number | null | undefined): void {
        if (proposalId == null) {
            return;
        }

        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: Read-only data widget for a dunning proposal — renders the backend
         *         FormObject field groups (Allgemein/Saldo/Freigabe/Verarbeitung/
         *         Verknüpfungen/Technische Daten) of bd.DunningProposal.
         */
        this.proposalDataWidget = WidgetFactory.createWidgetEntityData(
            'bd.dunningProposal.object.data',
            'Vorschlagsdaten',
            'bd.DunningProposal',
            proposalId
        );
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
